import { Button, Form, Input, message, Modal, Space } from "antd";
import { useCallback, useEffect, useState } from "react";
import coinImg from "../../assets/image/coin.png";
import { getPointRate, ISetPointRateRequest, setPointRate } from "../../api/points/points.api";
import "./CModalPointRate.styles.scss";

interface ICModalPointRateProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess?: () => void;
}

const CModalPointRate: React.FC<ICModalPointRateProps> = ({
    visible,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingFetch, setLoadingFetch] = useState(false);
    const [currentRate, setCurrentRate] = useState<number>(0);

    const fetchCurrentRate = useCallback(async () => {
        if (!visible) return;
        
        setLoadingFetch(true);
        try {
            const response = await getPointRate();
            setCurrentRate(response.rate);
            form.setFieldsValue({ rate: response.rate });
        } catch (error) {
            console.error("Error fetching point rate:", error);
            message.error("Không thể tải thông tin tỷ lệ điểm hiện tại");
        } finally {
            setLoadingFetch(false);
        }
    }, [visible, form]);

    useEffect(() => {
        if (visible) {
            fetchCurrentRate();
        }
    }, [visible, fetchCurrentRate]);

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const requestData: ISetPointRateRequest = {
                rate: Number(values.rate)
            };
            const response = await setPointRate(requestData);
            message.success(response.message || "Cập nhật tỷ lệ điểm thành công!");
            setCurrentRate(requestData.rate);
            onSuccess?.();
            onCancel();
        } catch (error) {
            console.error("Error setting point rate:", error);
            message.error("Có lỗi xảy ra khi cập nhật tỷ lệ điểm");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={
                <Space>
                    <img src={coinImg} alt="coin" style={{ width: 20, height: 20 }} />
                    <span>Cài đặt tỷ lệ quy đổi điểm</span>
                </Space>
            }
            visible={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
            className="modal-point-rate"
        >
            <div style={{ padding: "20px 0" }}>
                <div className="current-rate-display">
                    <div className="rate-label">
                        <img src={coinImg} alt="coin" style={{ width: 16, height: 16 }} />
                        <strong>Tỷ lệ hiện tại:</strong>
                    </div>
                    <div className="rate-value">
                        {loadingFetch ? "Đang tải..." : `${currentRate?.toLocaleString()} VNĐ = 1 điểm`}
                    </div>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ rate: currentRate }}
                >
                    <Form.Item
                        label="Tỷ lệ quy đổi mới (VNĐ)"
                        name="rate"
                        rules={[
                            { required: true, message: "Vui lòng nhập tỷ lệ quy đổi!" },
                            { 
                                validator: (_, value) => {
                                    const numValue = Number(value);
                                    if (isNaN(numValue) || numValue <= 0) {
                                        return Promise.reject(new Error("Tỷ lệ quy đổi phải là số lớn hơn 0!"));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input
                            type="number"
                            min={1}
                            placeholder="Nhập số tiền VNĐ tương ứng với 1 điểm"
                            suffix="VNĐ = 1 điểm"
                            style={{ fontSize: "16px" }}
                        />
                    </Form.Item>

                    <div className="example-note">
                        <div className="example-text">
                            <strong>Ví dụ:</strong> Nếu nhập 1500, nghĩa là 1500 VNĐ = 1 điểm
                        </div>
                    </div>

                    <Form.Item className="form-actions" style={{ marginBottom: 0 }}>
                        <Space>
                            <Button onClick={handleCancel}>
                                Hủy
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                            >
                                Cập nhật
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default CModalPointRate;
